import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText, FiUploadCloud, FiX, FiChevronLeft, FiClock } from "react-icons/fi";

const ACCEPTED_FILE_TYPES = ".pdf,.jpg,.jpeg,.png,.bmp";

function DocumentUploadDialog({ isOpen, onClose, onUpload, isUploading, documentTypes }) {
  const { t } = useTranslation("common");
  const [step, setStep] = useState("type");
  const [selectedType, setSelectedType] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setStep("type");
      setSelectedType("");
      setIsDragActive(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep("upload");
  };

  const openFilePicker = () => {
    if (!selectedType) return;
    fileInputRef.current?.click();
  };

  const handleFilePicked = (file) => {
    if (!file || !selectedType || isUploading) return;
    onUpload(file, selectedType);
    onClose();
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (!selectedType || isUploading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      handleFilePicked(file);
    }
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!selectedType || isUploading) return;
    setIsDragActive(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const selectedTypeLabel =
    documentTypes.find((docType) => docType.value === selectedType)?.label || "";

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="w-full max-w-xl min-h-[340px] sm:min-h-[400px] max-h-[92vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("uploadDocument")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-300">
              {step === "type" ? t("chooseDocumentTypeToContinue") : t("dropOrClickToUpload")}
            </p>
          </div>
          <button
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            onClick={onClose}
            type="button"
            aria-label={t("close")}
            disabled={isUploading}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 min-h-[260px] sm:min-h-[300px]">
          {step === "type" ? (
            <div className="h-full min-h-[220px] sm:min-h-[250px] flex items-center justify-center">
              <div className="w-full max-w-lg grid grid-cols-1 gap-3 sm:grid-cols-2">
                {documentTypes.map((docType) => {
                  const Icon = docType.value === "referral" ? FiFileText : FiClock;
                  return (
                    <button
                      key={docType.value}
                      type="button"
                      onClick={() => handleTypeSelect(docType.value)}
                      className="group min-h-[120px] rounded-xl border border-gray-200 bg-gray-50 p-4 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md dark:border-gray-600 dark:bg-gray-700/60 dark:hover:border-gray-500"
                    >
                      <div className="mb-3 inline-flex rounded-lg bg-white p-2 dark:bg-gray-800">
                        <Icon className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                      </div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {docType.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep("type")}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                  disabled={isUploading}
                >
                  <FiChevronLeft className="h-4 w-4" />
                  {t("backToTypeSelection")}
                </button>
                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                  {t("selectedDocumentTypeLabel")}: {selectedTypeLabel}
                </span>
              </div>

              <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={openFilePicker}
                className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all ${
                  isDragActive
                    ? "border-gray-500 bg-gray-100 dark:border-gray-400 dark:bg-gray-700"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700/40 dark:hover:bg-gray-700"
                }`}
              >
                <div className="mx-auto mb-3 inline-flex rounded-full bg-white p-3 dark:bg-gray-800">
                  <FiUploadCloud className="h-6 w-6 text-gray-700 dark:text-gray-200" />
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t("dropOrClickToUpload")}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                  {t("supportedFileTypes")}
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                className="hidden"
                onChange={(event) => handleFilePicked(event.target.files?.[0])}
                disabled={isUploading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentUploadDialog;
